import cx from "classnames";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import { trackingDataAttributes } from "utils/tracking";

import css from "./button.module.css";

type ButtonStyle =
  "rose" | "toggled" | "green" | "text" | "outline" | "default" | "blueLink";

type ButtonProps<T extends ElementType = "button"> = {
  children?: ReactNode;
  className?: string;
  component?: T;
  horizontalPadding?: string;
  loading?: boolean;
  style?: ButtonStyle;
  trackingEvent?: Record<string, unknown>;
  verticalPadding?: string;
};

type ButtonPropsWithElement<T extends ElementType> = ButtonProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps<T>>;

const styleClasses: Record<ButtonStyle, string> = {
  rose: "white hover-white bg-rose hover-bg-rose--light active-bg-rose--dark b--transparent",
  toggled: "link rose b--rose",
  green:
    "link body-dark hover-black bg-green hover-bg-light-green b--transparent",
  text: "link rose hover-rose--light b--transparent",
  outline: "link b--moon-gray",
  default: "link b--transparent",
  blueLink: "link fw4 blue--link b--transparent f5",
};

function Button<T extends ElementType = "button">({
  children,
  component = "button" as T,
  className,
  horizontalPadding = "ph3",
  loading,
  style = "default",
  trackingEvent,
  verticalPadding = "pv2",
  ...rest
}: ButtonPropsWithElement<T>) {
  const rootClassName = cx(
    css.root,
    "b br-pill f6 dib bg-animate tc sans-serif pointer ba relative no-underline",
    horizontalPadding,
    verticalPadding,
    styleClasses[style],
    css[style],
    className,
  );

  const Component = component;
  return (
    // @ts-expect-error TODO fix incorrect types - polymorphic component typing issue
    <Component
      {...rest}
      {...trackingDataAttributes(trackingEvent)}
      className={rootClassName}
    >
      {loading ? (
        <>
          <div className={css.spinner} />
          <div className="o-0">{children}</div>
        </>
      ) : (
        children
      )}
    </Component>
  );
}

export default Button;
